import React, { useEffect, useRef, useState } from 'react';
import './RowActionsMenu.css';
import { reportButtonStyle } from './ReportShell';
import { useAllowedItems } from './PermissionsContext';

// Standard toolbar-level "Actions ▾" dropdown for a form/detail page's
// secondary actions (Print, Refresh Prices, Duplicate, etc.) — distinct from
// RowActionsMenu (the list-view row kebab) and ReportLauncher (report-
// specific, wired to ReportOverlay). Save/Cancel stay outside this menu as
// the page's primary, solid-filled actions; this is only for the outlined
// secondary ones that would otherwise pile up as separate inline buttons.
// Reuses RowActionsMenu.css's dropdown styling and reportButtonStyle's
// trigger-button look for visual consistency with those two.
//
// `items`: [{ key, label, onSelect, disabled, disabledTitle, needs }] —
// filtered by the current screen's permissions, same as RowActionsMenu.
// `{ key, divider: true }` draws a horizontal rule between groups; one left
// leading, trailing or doubled up once permissions drop items is skipped.
// `placement="down"`: opens downward and right-aligned, for a menu in a
// page's header (SalesOrderPage.js) rather than beside Save/Cancel.
function ActionsMenu({ label = 'Actions ▾', items: allItems, ariaLabel = 'Actions', style, placement = 'up' }) {
  const allowed = useAllowedItems(allItems);
  const rows = allowed.filter((item, i) => !item.divider
    || (i > 0 && i < allowed.length - 1 && !allowed[i - 1].divider && allowed.slice(i + 1).some((next) => !next.divider)));
  const items = rows.filter((item) => !item.divider);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Same "grey out the trigger rather than open an unusable dropdown" rule
  // as RowActionsMenu when every item is disabled.
  const allDisabled = items.every((item) => item.disabled);
  const disabledTitle = allDisabled ? items.find((item) => item.disabledTitle)?.disabledTitle : undefined;

  if (!items.length) return null;

  return (
    <div className="row-actions-menu" ref={containerRef} style={style}>
      <button
        type="button"
        onClick={() => !allDisabled && setOpen((o) => !o)}
        disabled={allDisabled}
        title={disabledTitle}
        aria-label={ariaLabel}
        aria-haspopup="true"
        aria-expanded={open}
        style={reportButtonStyle(allDisabled)}
      >
        {label}
      </button>
      {open && (
        // Opens upward (bottom: 100%), not the shared class's default
        // downward open — this trigger sits at the bottom of the page
        // (next to Save/Cancel), where a downward dropdown can run off the
        // bottom of the viewport and need a scroll to see. RowActionsMenu's
        // own row-level "⋮" kebab elsewhere keeps the shared class's
        // default, unaffected by this override.
        <div
          className="row-menu-dropdown"
          style={placement === 'down'
            ? { right: 0, left: 'auto', top: '100%', bottom: 'auto', marginTop: 4, minWidth: 200, whiteSpace: 'nowrap' }
            : { right: 'auto', left: 0, top: 'auto', bottom: '100%', marginBottom: 4, minWidth: 200, whiteSpace: 'nowrap' }}
        >
          {rows.map((item) => (item.divider ? (
            <div key={item.key} role="separator" style={{ height: 1, background: '#e3e7ee', margin: '4px 0' }} />
          ) : (
            <button
              key={item.key}
              type="button"
              className="row-menu-item"
              disabled={item.disabled}
              title={item.disabled ? item.disabledTitle : undefined}
              onClick={() => {
                if (item.disabled) return;
                setOpen(false);
                item.onSelect();
              }}
            >
              {item.label}
            </button>
          )))}
        </div>
      )}
    </div>
  );
}

export default ActionsMenu;
